<template>
  <component
    :is="tag"
    ref="rootEl"
    class="pickable"
    :class="[
      { 'pickable--mode': isPickMode },
      variant === 'block' ? 'pickable--block' : 'pickable--inline',
    ]"
    :title="isPickMode ? label : undefined"
    @click.capture="onCaptureClick"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
  >
    <slot />
    <!-- Píldora flotante para secciones (block): aparece al hover -->
    <span
      v-if="isPickMode && hovered && variant === 'block'"
      class="pickable__chip"
      aria-hidden="true"
    >
      <i class="fas fa-pen"></i>
      Editar sección
    </span>
    <!-- Badge flotante para textos (inline): aparece al hover, sin mover el layout -->
    <span
      v-else-if="isPickMode && hovered && variant === 'inline'"
      class="pickable__badge"
      aria-hidden="true"
    >
      <i class="fas fa-pen"></i>
    </span>
  </component>
</template>

<script setup>
import { ref } from 'vue'
import { isPickMode } from '@/composables/usePickMode'

defineProps({
  label: { type: String, default: '' },
  tag: { type: String, default: 'span' },
  variant: { type: String, default: 'inline' },
})

const emit = defineEmits(['pick'])

const rootEl = ref(null)
const hovered = ref(false)

/**
 * En modo selección intercepta el clic antes que cualquier handler interno:
 * previene la navegación de <a> y la acción de componentes internos
 * (flechas de carrusel, acordeones, etc.) y emite el pick.
 *
 * Si el clic cae dentro de un elemento más específico (texto anidado con
 * clase .pickable o card con [data-pickable-card]), lo deja pasar para que
 * ese elemento emita su propio pick.
 */
function onCaptureClick(event) {
  if (!isPickMode.value) return
  const target = event.target
  if (target && typeof target.closest === 'function') {
    const nearest = target.closest('.pickable')
    if (nearest && rootEl.value && nearest !== rootEl.value) return
    const card = target.closest('[data-pickable-card]')
    if (card) return
  }
  event.preventDefault()
  event.stopPropagation()
  emit('pick')
}
</script>

<style scoped>
.pickable {
  position: relative;
  border-radius: 4px;
}

.pickable--mode {
  cursor: crosshair;
}

/* ── Block (secciones) ── */
.pickable--block.pickable--mode {
  outline: 2px dashed transparent;
  outline-offset: -2px;
  transition: outline-color 140ms ease, background-color 140ms ease;
}

.pickable--block.pickable--mode:hover {
  outline-color: rgba(127, 43, 195, 0.65);
  background-color: rgba(127, 43, 195, 0.05);
}

.pickable__chip {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 50;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.32rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  background: rgba(127, 43, 195, 0.94);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  pointer-events: none;
}

/* ── Inline (textos) ── */
.pickable--inline.pickable--mode {
  outline: 2px dashed transparent;
  outline-offset: 2px;
  transition: outline-color 140ms ease;
}

.pickable--inline.pickable--mode:hover {
  outline-color: rgba(127, 43, 195, 0.65);
}

.pickable__badge {
  position: absolute;
  top: -10px;
  right: -20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #7f2bc3;
  color: #fff;
  font-size: 9px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
  pointer-events: none;
}
</style>
